import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { featureFlags } from '../lib/featureFlags';
import { captureError } from '../lib/capture-error';
import './AuthPage.css';

const AccountSetupPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        setError(updateError.message);
      } else {
        setSuccess('Password set successfully! You can now sign in with your email and password.');
      }
    } catch (err) {
      setError('An unexpected error occurred');
      captureError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleLinkSocial = async (provider: 'google' | 'facebook' | 'apple') => {
    setError(null);
    setLoading(true);

    try {
      const { error: linkError } = await supabase.auth.linkIdentity({
        provider,
        options: {
          redirectTo: `${window.location.origin}/account-setup?linked=${provider}`,
        },
      });

      if (linkError) {
        setError(linkError.message);
        setLoading(false);
      }
      // If successful, user will be redirected to the provider
    } catch (err) {
      setError('An unexpected error occurred');
      captureError(err);
      setLoading(false);
    }
  };

  // Check for successful link on redirect back
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const linked = params.get('linked');
    if (linked) {
      setSuccess(`Successfully linked your ${linked.charAt(0).toUpperCase() + linked.slice(1)} account! You can now sign in with ${linked}.`);
      // Clean up URL
      window.history.replaceState({}, '', '/account-setup');
    }
  }, []);

  if (!user) {
    return (
      <div className="container my-5">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-5">
            <div className="card shadow">
              <div className="card-body p-5 text-center">
                <h2 className="mb-4">Account Setup</h2>
                <p className="text-muted mb-4">
                  Please sign in to set up your account.
                </p>
                <a href="/login" className="btn btn-primary">
                  <i className="fas fa-sign-in-alt me-2" aria-hidden></i>
                  Sign In
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container my-5">
      <div className="row justify-content-center">
        <div className="col-md-8 col-lg-6">
          <div className="card shadow">
            <div className="card-body p-5">
              <h2 className="text-center mb-2">Set Up Your Account</h2>
              <p className="text-center text-muted mb-4">
                Choose how you'd like to sign in to your account in the future.
              </p>

              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              {success && (
                <div className="alert alert-success" role="alert">
                  {success}
                </div>
              )}

              <div className="mb-4 p-3 bg-light rounded">
                <small className="text-muted">Signed in as:</small>
                <div className="fw-bold">{user.email}</div>
              </div>

              {/* Social Login Options */}
              <div className="mb-4">
                <h5 className="mb-3">Connect a Social Account</h5>
                <p className="text-muted small mb-3">
                  Link a social account to sign in quickly without a password.
                </p>

                <div className="d-grid gap-2">
                  {featureFlags.enableGoogleAuth && (
                    <button
                      type="button"
                      className="btn btn-outline-dark d-flex align-items-center justify-content-center"
                      onClick={() => handleLinkSocial('google')}
                      disabled={loading}
                    >
                      <i className="fab fa-google me-2"></i>
                      Connect with Google
                    </button>
                  )}
                  {featureFlags.enableFacebookAuth && (
                    <button
                      type="button"
                      className="btn btn-outline-primary d-flex align-items-center justify-content-center"
                      onClick={() => handleLinkSocial('facebook')}
                      disabled={loading}
                    >
                      <i className="fab fa-facebook me-2"></i>
                      Connect with Facebook
                    </button>
                  )}
                  {featureFlags.enableAppleAuth && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary d-flex align-items-center justify-content-center"
                      onClick={() => handleLinkSocial('apple')}
                      disabled={loading}
                    >
                      <i className="fab fa-apple me-2"></i>
                      Connect with Apple
                    </button>
                  )}

                  {!featureFlags.enableGoogleAuth && !featureFlags.enableFacebookAuth && !featureFlags.enableAppleAuth && (
                    <p className="text-muted small">
                      Social login options are not currently available. Please set a password below.
                    </p>
                  )}
                </div>
              </div>

              <div className="divider my-4">
                <span className="divider-text">or</span>
              </div>

              {/* Password Setup */}
              <div>
                <h5 className="mb-3">Set a Password</h5>
                <p className="text-muted small mb-3">
                  Create a password to sign in with your email address.
                </p>

                <form onSubmit={handleSetPassword}>
                  <div className="mb-3">
                    <label htmlFor="password" className="form-label">
                      New Password
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                      minLength={6}
                    />
                    <small className="form-text text-muted">
                      At least 6 characters
                    </small>
                  </div>

                  <div className="mb-3">
                    <label htmlFor="confirmPassword" className="form-label">
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      className="form-control"
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      disabled={loading}
                      minLength={6}
                    />
                  </div>

                  <button
                    type="submit"
                    className="btn btn-primary w-100"
                    disabled={loading || !password || !confirmPassword}
                  >
                    {loading ? (
                      'Setting password...'
                    ) : (
                      <>
                        <i className="fas fa-key me-2" aria-hidden></i>
                        Set Password
                      </>
                    )}
                  </button>
                </form>
              </div>

              <hr className="my-4" />

              <div className="text-center">
                <button
                  type="button"
                  className="btn btn-link"
                  onClick={() => navigate('/dashboard')}
                >
                  Skip for now - Go to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountSetupPage;
